import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BookOpen, Plus, Pencil, Trash2, Save, X, Sparkles } from "lucide-react";

const CATS = ["Bedrijf", "Product", "Bereiding", "Houdbaarheid", "Bewaren", "Algemeen"];
const EMPTY = { id: null, categorie: "Algemeen", titel: "", inhoud: "" };

export default function KennisbankTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/sales/kb").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.titel.trim() && !form.inhoud.trim()) return toast.error("Vul minstens een titel of inhoud in.");
    setBusy(true);
    try {
      await api.post("/sales/kb", form);
      toast.success(form.id ? "Kennis bijgewerkt" : "Kennis toegevoegd — Larissa gebruikt dit direct");
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Dit kennisitem verwijderen?")) return;
    try { await api.delete(`/sales/kb/${id}`); toast.success("Verwijderd"); if (form.id === id) setForm(EMPTY); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const grouped = items.reduce((acc, it) => { (acc[it.categorie] = acc[it.categorie] || []).push(it); return acc; }, {});
  const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      {/* Editor */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 h-fit lg:sticky lg:top-24" data-testid="kb-editor">
        <div className="flex items-center gap-2 mb-1"><BookOpen className="h-5 w-5 text-blue-700" /><h3 className="text-base font-bold text-slate-900">{form.id ? "Kennis bewerken" : "Kennis toevoegen"}</h3></div>
        <p className="text-xs text-slate-500 mb-4">Voed hier de kennis van assistent <b>Larissa</b>. Alles wat je toevoegt gebruikt de bot automatisch en direct bij het beantwoorden van vragen. Larissa kent daarnaast al de wetgeving voor snackproducenten en kan uitleg geven over het gebruik van het CRM-systeem.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Categorie</label>
            <input list="kb-cats" data-testid="kb-categorie" className={inp} value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} placeholder="Bijv. Houdbaarheid" />
            <datalist id="kb-cats">{CATS.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Titel / onderwerp</label>
            <input data-testid="kb-titel" className={inp} value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} placeholder="Bijv. Houdbaarheid gepaneerde kipfilet" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Informatie</label>
            <textarea data-testid="kb-inhoud" rows={7} className={inp} value={form.inhoud} onChange={(e) => setForm({ ...form, inhoud: e.target.value })} placeholder="Schrijf hier de feiten, cijfers of instructies die Larissa moet weten…" />
          </div>
          <div className="flex gap-2">
            <Button data-testid="kb-save" onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800"><Save className="h-4 w-4 mr-1" /> {form.id ? "Opslaan" : "Toevoegen"}</Button>
            {form.id && <Button variant="outline" data-testid="kb-cancel" onClick={() => setForm(EMPTY)}><X className="h-4 w-4 mr-1" /> Annuleren</Button>}
          </div>
        </div>
      </div>

      {/* List */}
      <div>
        <div className="flex items-center gap-2 mb-3 text-slate-500 text-sm"><Sparkles className="h-4 w-4 text-amber-500" /> Larissa's geheugen — {items.length} kennisitem{items.length === 1 ? "" : "s"}</div>
        {items.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-400" data-testid="kb-empty">Nog geen kennis toegevoegd. Voeg links het eerste item toe.</div>}
        <div className="space-y-5">
          {Object.keys(grouped).sort().map((cat) => (
            <div key={cat}>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-blue-700">{cat}</div>
              <div className="space-y-2">
                {grouped[cat].map((it) => (
                  <div key={it.id} data-testid="kb-item" className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800">{it.titel || <span className="italic text-slate-400">Zonder titel</span>}</div>
                        <div className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600">{it.inhoud}</div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button data-testid="kb-edit" title="Bewerken" onClick={() => setForm({ id: it.id, categorie: it.categorie, titel: it.titel, inhoud: it.inhoud })} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                        <button data-testid="kb-delete" title="Verwijderen" onClick={() => remove(it.id)} className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
